import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HighlightOff from '@mui/icons-material/HighlightOff';
import { TreeItem, TreeItemProps, TreeView } from '@mui/lab';
import { Dialog, DialogActions, DialogContent, Grid, SvgIcon, SvgIconProps, TextField, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { GlobalClassKeys } from 'assets/jss/common-styles';
import ButtonClose from 'components/Buttons/ButtonClose';
import ButtonDelete from 'components/Buttons/ButtonDelete';
import ButtonEdit from 'components/Buttons/ButtonEdit';
import { SpinnerIndicator } from 'components/CallServer';
import MuiClickable from 'components/MuiExtras/MuiClickable';
import MuiConfirmation from 'components/MuiExtras/MuiConfirmation';
import MuiDialogTitleClose from 'components/MuiExtras/MuiDialogTitleClose';
import LibUtils from 'lib/LibUtils';
import { TopicTreeItemDto } from 'models/ApiModels';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { isArray } from 'util';
/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface TopicTreeStyles extends React.CSSProperties {
    '--tree-view-color'?: string;
    '--tree-view-bg-color'?: string;
}

const styles = (theme: Theme) => createStyles({
    dialogPaper: {
        minHeight: '80vh',
        maxHeight: '80vh',
    },
    root: {
        flexGrow: 1,
        maxWidth: "100%"
    },
});

export interface IState {
    loading: boolean;
    data: TopicTreeItemDto[];
    dialog: any;
    expanded: string[];
    selected: string[];
    searchText: string;
    searchedNodes: string[];
    currentSearchedIndex: number;
}

interface IProps {
    rootId?: string;
    rootName?: string;
    onDelete: (id: string) => Promise<boolean>;
    onEdit: (id: string) => void;
    onClose: () => void;
}

type Props = IProps & WithStyles<typeof styles> & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

type StyledTreeItemProps = TreeItemProps & {
    bgColor?: string;
    color?: string;
    labelIcon?: React.ElementType<SvgIconProps>;
    labelInfo?: string;
    labelText: string;
};

const useTreeItemStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            color: theme.palette.text.secondary,
            '&$selected > $content': {
                color: 'var(--tree-view-color)',
                width: "fit-content",
            },
            '&:focus > $content': {
                //backgroundColor: `var(--tree-view-bg-color, ${theme.palette.grey[400]})`,
                //color: 'var(--tree-view-color)',
                width: "fit-content"
            },
            '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
                backgroundColor: 'transparent',
                width: "fit-content"
            },
            '&$selected:focus > $content $label, &$selected > $content $label': {
                color: theme.palette.info.contrastText,
                backgroundColor: theme.palette.info.main
            }
        },
        content: {
            color: theme.palette.text.secondary,
            paddingRight: theme.spacing(1),
            fontWeight: theme.typography.fontWeightMedium,
            '$expanded > &': {
                fontWeight: theme.typography.fontWeightRegular,
            },
        },
        group: {
            '& $content': {
                paddingLeft: theme.spacing(2),
            },
        },
        expanded: {},
        selected: {},
        label: {
            fontWeight: 'inherit',
            color: 'var(--tree-view-color)',
            width: "auto"
        },
        labelRoot: {
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(0.5, 0),
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
                width: "fit-content"
            }
        },
        labelIcon: {
            marginRight: theme.spacing(1),
        },
        labelText: {
            fontWeight: 'inherit',
            flexGrow: 1,
        },
        iconContainer: {
            '& .close': {
                opacity: 0.3,
            },
        },

    }),
);

function StyledTreeItem(props: StyledTreeItemProps) {
    const classes = useTreeItemStyles();
    const { labelText, labelIcon: LabelIcon, labelInfo, color, bgColor, ...other } = props;

    return (
        <TreeItem
            label={
                <div className={classes.labelRoot}>
                    {LabelIcon && <LabelIcon color="inherit" className={classes.labelIcon} />}
                    <Typography variant="body2" className={classes.labelText}>
                        {labelText}
                    </Typography>
                    <Typography variant="caption" color="inherit">
                        {labelInfo}
                    </Typography>
                </div>
            }
            style={
                {
                    '--tree-view-color': color,
                    '--tree-view-bg-color': bgColor,
                } as TopicTreeStyles
            }
            classes={{
                root: classes.root,
                content: classes.content,
                expanded: classes.expanded,
                selected: classes.selected,
                group: classes.group,
                label: classes.label,
            }}
            {...other}
        />
    );
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

function MinusSquare(props: SvgIconProps) {
    return (
        <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
            {/* tslint:disable-next-line: max-line-length */}
            <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
        </SvgIcon>
    );
}

function PlusSquare(props: SvgIconProps) {
    return (
        <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
            {/* tslint:disable-next-line: max-line-length */}
            <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
        </SvgIcon>
    );
}

function CloseSquare(props: SvgIconProps) {
    return (
        <SvgIcon className="close" fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
            {/* tslint:disable-next-line: max-line-length */}
            <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
        </SvgIcon>
    );
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class TopicTreeDialog extends React.PureComponent<Props, IState> {

    private searchRef: any;

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            loading: true,
            data: null,
            expanded: props.rootId ? [props.rootId] : [],
            selected: [],
            searchText: "",
            searchedNodes: [],
            currentSearchedIndex: -1
        };
    }

    ///
    private loadData() {
        this.setState({ loading: true, data: null });

        api.getTopicTree(this.props.rootId)
            .then((data) => {

                this.setState({ loading: false, data: data });
            })
            .catch(() => {
                this.setState({ loading: false });
            });
    }

    //
    private handleDelete() {
        var node = this.state.data.find(p => p.topicId == this.state.selected[0]);
        this.setState({
            dialog:
                <MuiConfirmation
                    title={this.props.t("Common.Buttons.DeleteText")}
                    message={this.props.t("Common.Question.ConfirmationDeleteOfMessage", { name: node.name })}
                    onClose={
                        (result) => {
                            if (result) {
                                this.props.onDelete(node.topicId)
                                    .then((result) => {
                                        this.setState({ dialog: null }, () => {
                                            if (result) this.loadData();
                                        });
                                    })
                                    .catch((error) => {
                                        this.setState({ dialog: null });
                                    });
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
        });
    }

    ///
    private handleToggle = (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
        this.setState({ expanded: isArray(nodeIds) ? nodeIds : [nodeIds] });
    };

    ///
    private handleSelect = (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
        this.setState({ selected: isArray(nodeIds) ? nodeIds : [nodeIds] });
    };

    ///
    private handleSearch(e: React.KeyboardEvent<HTMLDivElement>) {
        if (this.searchRef.value.length > 0) {
            if (e.keyCode == 13) {
                const text = LibUtils.removeAccents(this.state.searchText.toLowerCase());
                const searchedNodes = this.state.data.filter(x => LibUtils.removeAccents(x.name.toLowerCase()).indexOf(text) > -1).map((v) => v.topicId);
                if (searchedNodes.length > 0) {
                    this.setState({ searchedNodes, currentSearchedIndex: 0 }, () => this.handleNextSearch(0));
                }
                else {
                    this.setState({ searchedNodes, currentSearchedIndex: 0 });
                }
            }
            else {
                this.setState({ currentSearchedIndex: -1 });
            }
        }
    }

    ///
    private handleClearSearch() {
        this.setState({ searchText: "", currentSearchedIndex: -1, searchedNodes: [] }, () => this.searchRef.focus());
    }

    ///
    private handleNextSearch(nextIndex: number) {
        this.setState((state) => {
            const selected = [state.searchedNodes[nextIndex]];
            const expanded = [];
            let parent = state.data.find(x => x.topicId == selected[0]);
            while (parent != null) {
                expanded.push(parent.parentId);
                parent = state.data.find(x => x.topicId == parent.parentId);
            }
            return { ...state, currentSearchedIndex: nextIndex, expanded, selected };
        });
    }

    ///
    private buildTreeItem(rootId: string): React.ReactNode {
        const nodes = this.state.data.filter(x => x.parentId == rootId);

        return nodes.map((node) => {
            return (
                <StyledTreeItem key={node.topicId} nodeId={node.topicId} labelText={node.name} color={node.isPublic ? "black" : "orange"} >
                    {this.buildTreeItem(node.topicId)}
                </StyledTreeItem>
            );
        });
    }

    ///
    private renderSearch() {
        const { searchText, searchedNodes, currentSearchedIndex } = this.state;

        return (
            <Grid container wrap="nowrap" justifyContent="flex-end" alignItems="center">
                <Grid item xs>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="none"
                        error={searchText.length > 0 && searchedNodes.length == 0 && currentSearchedIndex != -1}
                        placeholder={this.props.t("Common.Input.Placeholder.SearchAndEnter")}
                        value={searchText}
                        onChange={() => this.setState({ searchText: this.searchRef.value })}
                        onKeyDown={(e) => this.handleSearch(e)}
                        inputRef={(ref) => this.searchRef = ref}
                    />
                </Grid>

                {
                    currentSearchedIndex != -1 &&
                    <>
                        <Grid item>
                            {
                                searchedNodes.length > 0 &&
                                <b>({currentSearchedIndex + 1} / {searchedNodes.length})</b>
                            }
                        </Grid>
                        <Grid item>
                            {
                                searchedNodes.length > 1 &&
                                <>
                                    <MuiClickable
                                        disabled={currentSearchedIndex == 0}
                                        onClick={() => this.handleNextSearch(currentSearchedIndex - 1)}>
                                        <ChevronLeftIcon />
                                    </MuiClickable>
                                    <MuiClickable
                                        disabled={currentSearchedIndex == searchedNodes.length - 1}
                                        onClick={() => this.handleNextSearch(currentSearchedIndex + 1)}>
                                        <ChevronRightIcon />
                                    </MuiClickable>
                                </>
                            }
                            {
                                searchedNodes.length > 0 &&
                                <MuiClickable onClick={() => this.handleClearSearch()}>
                                    <HighlightOff />
                                </MuiClickable>
                            }
                        </Grid>
                    </>
                }

            </Grid>
        );
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { expanded, selected } = this.state;

        return (
            <Dialog
                open
                fullWidth
                onClose={() => { this.props.onClose(); }}
                maxWidth="lg"
                classes={{ paper: this.props.classes.dialogPaper }}
            >
                <MuiDialogTitleClose
                    title={this.props.t("Views.Topic.Dialogs.TopicTreeDialog.BoxTitle")}
                    subtitle={this.props.rootName}
                    onClose={() => { this.props.onClose(); }}
                />

                <DialogContent style={{ display: "flex" }}>
                    {this.state.dialog}
                    {
                        this.state.loading
                            ? <SpinnerIndicator />
                            : (
                                <Grid container direction="row" style={{ overflow: "hidden" }}>
                                    <Grid item style={{ width: "100%" }}>
                                        {this.renderSearch()}
                                    </Grid>
                                    <Grid item style={{ width: "100%", height: "100%", maxHeight: "calc(100% - 64px)", overflowY: "auto" }}>
                                        <TreeView
                                            className={this.props.classes.root}
                                            defaultCollapseIcon={<MinusSquare />}
                                            defaultExpandIcon={<PlusSquare />}
                                            defaultEndIcon={<CloseSquare />}
                                            expanded={expanded}
                                            selected={selected}
                                            onNodeToggle={this.handleToggle}
                                            onNodeSelect={this.handleSelect}
                                        >
                                            {this.buildTreeItem(null)}
                                        </TreeView>
                                    </Grid>
                                </Grid>
                            )
                    }
                </DialogContent>

                <DialogActions>
                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                        <Grid item>
                            <ButtonDelete disabled={selected.length == 0} onClick={() => this.handleDelete()} color="error" />
                        </Grid>
                        <Grid item xs style={{ height: 48 }}>
                            <div id="nodeInfo"></div>
                        </Grid>
                        <Grid item className={GlobalClassKeys.textRight}>
                            <ButtonEdit disabled={selected.length == 0} onClick={() => this.props.onEdit(this.state.selected[0])} color="info" />
                            <ButtonClose onClick={() => this.props.onClose()} color="default" />
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog >
        );
    }
}

export default withTranslation()(withStyles(styles)(TopicTreeDialog));